// Rule 5: nothing typed is lost. One hook gives an editor its fields with a phone-side copy that
// is written as the person types, restored on return (with a toast), and cleared on file/discard.
import { useEffect, useState } from 'react';
import { toast } from '../app/toast';
import { readLocalDraft, useLocalDraft, clearLocalDraft } from '../app/localDraft';

export interface FormOpts<T extends object> {
  key: string | null;                 // tv.draft.<type>.<pid>.<date>
  initial: T;                         // blank or from the server copy
  resumeId?: string | null;           // when continuing a chosen draft, a phone copy of a different draft is ignored
  restoredMessage?: string;
}

export function useLocalForm<T extends object & { id?: string | null }>({ key, initial, resumeId, restoredMessage }: FormOpts<T>) {
  const [saved] = useState<T | null>(() => { const v = readLocalDraft<T>(key); return v && resumeId && v.id !== resumeId ? null : v; });
  const [form, setForm] = useState<T>(() => saved ? { ...initial, ...saved } : initial);
  useLocalDraft<T>(key, form);
  useEffect(() => {
    if (!saved) return;
    const typed = Object.entries(saved).some(([k, v]) => k !== 'id' && typeof v === 'string' && v.trim());
    if (typed) toast(restoredMessage ?? 'Restored what you had typed before the app reloaded. If you took a photo, take it again.');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = <K extends keyof T>(k: K, v: T[K]) => setForm(f => ({ ...f, [k]: v }));
  const clear = () => clearLocalDraft(key);
  return { form, set, setForm, clear, restored: !!saved };
}
